import {
  IMessage,
  MessageStatus,
  chatMessagesAtom
} from '@/features/Chat/state';
import { getWelcomeMessageHtml } from '@/features/Chat/welcomeMessage';
import { LanguageOptions, Languages } from '@/translations/languages';
import { WidgetStorageId } from '@/types/enums/WidgetStorageId';
import { useAtom } from 'jotai';
import storage from 'local-storage-fallback';

import i18n from '../../../i18n';
import { widgetLanguageAtom } from './widgetLanguage';

const useWidgetLanguage = () => {
  const [widgetLanguage, setWidgetLanguage] = useAtom(widgetLanguageAtom);
  const [_, setChatMessages] = useAtom(chatMessagesAtom);

  const initWidgetLanguage = (preferedWidgetLanguage?: string) => {
    const userLanguagePreference = storage.getItem(
      WidgetStorageId.WidgetLanguage
    );

    let widgetLanguage;

    if (userLanguagePreference) {
      const validUserLanguagePreference = validateLanguageOption(
        userLanguagePreference
      );
      widgetLanguage = validUserLanguagePreference;
    }

    if (!widgetLanguage && preferedWidgetLanguage) {
      const validPreferedLanguage = validateLanguageOption(
        preferedWidgetLanguage
      );
      widgetLanguage = validPreferedLanguage;
    }

    if (!widgetLanguage) {
      const validPreferedLanguage = validateLanguageOption(navigator.language);
      widgetLanguage = validPreferedLanguage;
    }

    const languageValue = widgetLanguage || Languages.en;
    i18n.changeLanguage(languageValue);
    setWidgetLanguage({
      widgetLanguage: languageValue
    });
  };

  const changeWidgetLanguage = (
    languageValue: Languages,
    notifyUser = false
  ) => {
    try {
      if (languageValue) {
        i18n.changeLanguage(languageValue);
        setWidgetLanguage({
          widgetLanguage: languageValue
        });

        storage.setItem(WidgetStorageId.WidgetLanguage, languageValue);

        const tNext = i18n.getFixedT(languageValue, 'chat');

        setChatMessages(chatMessages => {
          const userHasWrittenAMessage = chatMessages.messages.some(
            m => m.isUserMessage
          );

          if (!userHasWrittenAMessage) {
            return {
              ...chatMessages,
              messages: chatMessages.messages.map(m =>
                m.contextRequest
                  ? {
                      ...m,
                      message: getWelcomeMessageHtml(
                        tNext,
                        m.contextRequest.searchRequest
                      )
                    }
                  : m
              )
            };
          }

          if (!notifyUser) {
            return chatMessages;
          }

          const responseMessage: IMessage = {
            id: `${Date.now()}-S`,
            isUserMessage: false,
            message: tNext('changeLanguageMessage'),
            status: MessageStatus.Received
          };
          return {
            chatId: chatMessages.chatId,
            messages: chatMessages.messages.concat([responseMessage])
          };
        });
      } else {
        storage.setItem(WidgetStorageId.WidgetLanguage, Languages.en);
        console.error(
          `Provided language is not supported by the widget. Defaulting to en. Supported languages are: ${LanguageOptions}.`
        );
      }
    } catch (ex) {
      storage.setItem(WidgetStorageId.WidgetLanguage, Languages.en);
      console.error(
        `Invalid language or locale provided (${languageValue}). Defaulting to en.`
      );
    }
  };

  function validateLanguageOption(preferedLanguage: string) {
    try {
      Intl.getCanonicalLocales([preferedLanguage]);

      const locale = new Intl.Locale(preferedLanguage);
      const language = locale.language;

      const languageValue = LanguageOptions.find(lo => lo == language);
      return languageValue;
    } catch {
      return null;
    }
  }

  return { widgetLanguage, initWidgetLanguage, changeWidgetLanguage };
};

export default useWidgetLanguage;
